<template lang="html">
  <div class="dashboard">
    <header>
      <Alerts
        :alerts="notifications"
        @close-alert=closeAlert></Alerts>

      <div class="header">
        <router-link :to="{ name: 'dashboard', params: { locale: $i18n.locale } }">
          <img class="header-logo" src="../../src/assets/telecloud-logo.png">
        </router-link>

        <nav class="header-nav" :class="{ 'has-menu-open': isMenuOpen }">
          <ul class="header-nav-main show-mob">
            <li v-if="user && user.is_admin" :class="{ 'active': isActiveRoute('administration') }">
              <router-link :to="{name: 'administration', params: { locale: $i18n.locale }}">{{ $t('views.page.header.administration') }}</router-link>
            </li>
            <li :class="{ 'active': isActiveRoute('administration') }">
              <router-link :to="{name: 'account', params: { locale: $i18n.locale }}">{{ $t('views.page.header.account') }}</router-link>
            </li>
            <li>
              <a @click="logout">{{ $t('views.page.header.logout') }}</a>
            </li>
          </ul>
          <button
            @click="isMenuOpen=!isMenuOpen"
            class="button is-rounded is-square is-white is-xsmall hide-desk">
            <span class="css-icon is-burger"><span></span></span>
          </button>
        </nav>
      </div>
    </header>
    <main class="main has-side-nav">
        <nav :class="{ 'is-menu-open': isMenuOpen}" class="side-nav">
          <ul class="nav">
            <li class="menu" :class="{ 'is-active': isActiveRoute('dashboard') }">
              <router-link :to="{name: 'dashboard', params: { locale: $i18n.locale }}">{{ $t('views.page.nav.dashboard') }}</router-link>
            </li>
            <li
              class="menu has-children"
              v-if="hasAgilityAccess && viewingAgilityPages"
              :class="{
                'is-open': toggleMenu['agility'],
                'is-active': isActiveRoutes(['agilityMonitoring', 'agilityVms'])}">
              <a v-on:click="toggle('agility')">
                  {{ $t('views.page.nav.agility') }}
                  <span class="toggler"></span>
              </a>
              <ul class="w-full">
                <li class="menu-item" :class="{ 'is-active': isActiveRoute('agilityMonitoring') }">
                  <router-link :to="{name: 'agilityMonitoring', params: { locale: $i18n.locale }}">{{ $t('views.page.nav.monitoring') }}</router-link>
                </li>
                <li class="menu-item" :class="{ 'is-active': isActiveRoute('agilityVms') }">
                  <router-link :to="{name: 'agilityVms', params: { locale: $i18n.locale }}">{{ $t('views.page.nav.vms') }}</router-link>
                </li>
                <li class="menu-item">
                  <button
                    v-if="user && vmCreatePermission && hasSignedAdditionalContract"
                    v-on:click.prevent="openModal('add-edit-vm')"
                    class="button is-gamma is-xsmall">
                    <i class="icon-add"></i>
                    {{ $t('views.page.nav.addVm') }}
                  </button>
                </li>
              </ul>
            </li>
            <li
              class="menu has-children"
              v-if="hasDirectorAccess && viewingDirectorPages"
              :class="{
                'is-open': toggleMenu['director'],
                'is-active': isActiveRoutes(['directorPools', 'directorVms']),
                'has-children': true}">
              <a v-on:click="toggle('director')">
                  {{ $t('views.page.nav.director') }}
                  <span class="toggler"></span>
              </a>
              <ul class="w-full">
                <li class="menu-item" :class="{ 'is-active': isActiveRoute('directorPools') }">
                  <router-link :to="{name: 'directorPools', params: { locale: $i18n.locale }}">{{ $t('views.page.nav.pools') }}</router-link>
                </li>
                <li class="menu-item" :class="{ 'is-active': isActiveRoute('directorVms') }">
                  <router-link :to="{name: 'directorVms', params: { locale: $i18n.locale }}">{{ $t('views.page.nav.vms') }}</router-link>
                </li>
                <li class="menu-item" :class="{ 'is-active': isActiveRoute('directorEdges') }">
                  <router-link :to="{name: 'directorEdges', params: { locale: $i18n.locale }}">{{ $t('views.page.nav.edges') }}</router-link>
                </li>
                <li v-if="user && user.is_admin" class="menu-item">
                  <button
                    v-if="poolCreatePermission && hasSignedAdditionalContract"
                    v-on:click.prevent="openModal('add-edit-pool')"
                    class="button is-gamma is-xsmall">
                    <i class="icon-add"></i>
                    {{ $t('views.page.nav.addPool') }}
                  </button>
                </li>
              </ul>
            </li>
            <li
              class="menu has-children"
              v-if="hasBackupAccess && (viewingBackupsActivity || viewingBackupsDevices)"
              :class="{
                'is-open': toggleMenu['backup'],
                'is-active': isActiveRoute('backup')}">
              <a v-on:click="toggle('backup')">
                  {{ $t('views.page.nav.backup') }}
                  <span class="toggler"></span>
              </a>
              <ul>
                <li v-if="viewingBackupsActivity" class="menu-item" :class="{ 'is-active': isActiveRoute('backup') }">
                  <router-link :to="{name: 'backup', params: { locale: $i18n.locale }}">{{ $t('views.page.nav.activity') }}</router-link>
                </li>
                <li v-if="viewingBackupsDevices" class="menu-item" :class="{ 'is-active': isActiveRoute('devices') }">
                  <router-link :to="{name: 'devices', params: { locale: $i18n.locale }}">{{ $t('views.page.nav.devices') }}</router-link>
                </li>
                <li class="menu-item" :class="{ 'is-active': isActiveRoute('snapshots') }">
                  <router-link :to="{name: 'snapshots', params: { locale: $i18n.locale }}">{{ $t('views.page.nav.snapshots') }}</router-link>
                </li>

              </ul>
            </li>
            <li
              class="menu"
              :class="{ 'is-active': isActiveRoute('support') }">
              <router-link :to="{name: 'support', params: { locale: $i18n.locale }}">{{ $t('views.page.nav.support') }}</router-link>
            </li>
            <li
              v-if="user && viewingInvoices" class="menu"
              :class="{ 'is-active': isActiveRoute('invoices') }">
              <router-link :to="{name: 'invoices', params: { locale: $i18n.locale }}">{{ $t('views.page.nav.invoices') }}</router-link>
            </li>
            <li
              v-if="user && user.is_admin"
              class="menu hide-mob"
              :class="{ 'is-active': isActiveRoute('administration') }">
              <router-link :to="{name: 'administration', params: { locale: $i18n.locale }}">{{ $t('views.page.header.administration') }}</router-link>
            </li>
            <li
              class="menu hide-mob"
              :class="{ 'is-active': isActiveRoute('account') }">
              <router-link :to="{name: 'account', params: { locale: $i18n.locale }}">{{ $t('views.page.header.account') }}</router-link>
            </li>
            <li class="menu">
              <a @click="emitOpenModal('modal-cgu', 'already-accepted');" target="_blank">{{ $t('views.page.nav.terms') }}</a>
            </li>
            <li class="menu hide-mob"
                @click="logout"
                :class="{ 'is-active': isActiveRoute('logout') }">
              <a href="javascript:void(0)">{{ $t('views.page.header.logout') }}</a>
            </li>
          </ul>
        </nav>
        <div class="overflow-s h-full">
          <div class="container" @click="isMenuOpen=false">
            <h1 class="title-h1">{{ $t(title) }}</h1>
            <h2
              v-if="subtitle"
              class="title-h2">{{ $t(subtitle) }}</h2>
            <router-view class="pb-30"/>
            <Toasters></Toasters>

          </div>
        </div>
    </main>

    <ModalAddEditVm/>
    <ModalActionVm/>
    <ModalDeleteVm/>
    <ModalAddEditPool/>
    <ModalDeletePool/>
    <ModalAddEditEdge/>
    <ModalAddPublicIp/>
    <ModalOpenTicket/>
    <ModalCgu/>

    <ModalLogin/>

  </div>
</template>

<script lang="js">
import { mapGetters } from 'vuex';

import moment from 'moment';

import NOTIF from '@/api/notifications';

import Alerts from '@/components/Alerts.vue';
import Toasters from '@/components/Toasters.vue';

import ModalAddEditPool from '@/components/ModalAddEditPool.vue';
import ModalAddEditEdge from '@/components/ModalAddEditEdge.vue';
import ModalAddPublicIp from '@/components/ModalAddPublicIp.vue';
import ModalAddEditVm from '@/components/ModalAddEditVm.vue';
import ModalActionVm from '@/components/ModalActionVm.vue';

import ModalDeleteVm from '@/components/ModalDeleteVm.vue';
import ModalDeletePool from '@/components/ModalDeletePool.vue';
import ModalOpenTicket from '@/components/ModalOpenTicket.vue';
import ModalCgu from '@/components/ModalCgu.vue';

import ModalLogin from '@/components/ModalLogin.vue';
import { eventHubMixin } from '@/mixins/event';

export default {
  name: 'Page',
  mixins: [eventHubMixin],
  components: {
    Toasters,
    Alerts,
    ModalAddEditVm,
    ModalActionVm,
    ModalDeleteVm,
    ModalAddEditPool,
    ModalAddEditEdge,
    ModalAddPublicIp,
    ModalDeletePool,
    ModalOpenTicket,
    ModalCgu,
    ModalLogin,
  },
  data() {
    return {
      toggleMenu: {},
      title: '',
      currentRouteName: '',
      subtitle: '',
      isMenuOpen: false,
      notifications: [
      ],
      alerts: [],
    };
  },
  computed: {
    ...mapGetters({
      user: 'auth/user',
      hasAgilityAccess: 'auth/hasAgilityAccess',
      viewingAgilityPages: 'auth/viewingAgilityPages',
      hasBackupAccess: 'auth/hasBackupAccess',
      hasDirectorAccess: 'auth/hasDirectorAccess',
      viewingDirectorPages: 'auth/viewingDirectorPages',
      hasSignedAdditionalContract: 'auth/hasSignedAdditionalContract',
      viewingBackupsDevices: 'auth/viewingBackupsDevices',
      viewingBackupsActivity: 'auth/viewingBackupsActivity',
      viewingInvoices: 'auth/viewingInvoices'

    }),

    vmCreatePermission() {
      if (this.user.is_admin || this.user.roles_obj.some(role => role.opening_vm_creation_ticket)) {
        return true
      }
      return false
    },

    poolCreatePermission() {
      if (this.user.is_admin || this.user.roles_obj.some(role => role.opening_pool_create_ticket)) {
        return true
      }
      return false
    }
  },

  beforeRouteUpdate(to, from, next) {
    this.init(to);
    next();
  },

  created() {
    this.init(this.$route);
    this.getNotifications();
  },

  methods: {
    closeAlert(alert) {
      this.notifications = this.notifications.filter(elem => elem != alert);
    },

    getNotifications() {
      NOTIF.get().then(
        (res) => {
          this.notifications = res.data;
        },
      );
    },

    format_datetime(datetime) {
      return moment(datetime).format('DD/MM/YYYY hh:mm');
    },

    isActiveRoute(name) {
      return name === this.currentRouteName;
    },

    isActiveRoutes(names) {
      return names.indexOf(this.currentRouteName) > -1;
    },

    init(routeTo) {
      this.setToggleMenu();
      this.title = routeTo.meta.title;
      this.subtitle = (routeTo.meta.subtitle) ? routeTo.meta.subtitle : '';
      this.currentRouteName = routeTo.name;
    },

    setToggleMenu() {
      // retrieve current toggle state from localStorage
      const toggleMenu = localStorage.getItem('th-toggleMenu');

      if (toggleMenu) {
        this.toggleMenu = JSON.parse(toggleMenu);
      } else {
        this.toggleMenu = {
          agility: false,
          director: false,
          backup: false,
        };
      }
    },

    toggle(routeName) {
      if (this.toggleMenu[routeName]) {
        this.toggleMenu[routeName] = !this.toggleMenu[routeName];
      } else {
        this.toggleMenu[routeName] = true;
      }
      localStorage.setItem('th-toggleMenu', JSON.stringify(this.toggleMenu));
    },

    openModal(modalName) {
      this.eventHub.$emit(`modal:${modalName}:open`);
    },

    logout() {
      this.$store.dispatch('auth/logout').then(() => {
        this.$router.replace({ name: 'login', params: { locale: this.$i18n.locale } });
      });
    },
  },
};
</script>

<style lang="scss" scoped>

.dashboard {
  display: flex;
  flex-direction: column;
  height: 100vh;
}

</style>
